import { useState, type FormEvent } from 'react';
import type { ModoJogo, PartidaArquivada, Preferencias } from '../types';
import { autenticarAdmin } from '../lib/adminAuth';
import { Icone } from './Icone';
import { Peca } from './Peca';

interface InicioProps {
  preferencias: Preferencias;
  existePartida: boolean;
  historico?: PartidaArquivada[];
  aoEscolherModo: (modo: ModoJogo) => void;
  aoRetomar: () => void;
  aoDescartarPartida?: () => void;
  aoAbrirAdministracao: () => void;
  aoAbrirPreferencias: () => void;
  aoAlternarSom: () => void;
  aoAbrirSala?: () => void;
  aoTrocarNome?: () => void;
  aoEscolherNome?: (nome: string) => void;
  aoRemoverNome?: (nome: string) => void;
}

const modos: Array<{
  id: ModoJogo;
  numero: string;
  titulo: string;
  subtitulo: string;
  descricao: string;
  icone: 'pessoas' | 'xadrez' | 'modelo' | 'servidor' | 'olho' | 'teste';
}> = [
  {
    id: 'classico',
    numero: '01',
    titulo: 'Jogador vs CPU',
    subtitulo: 'Contra o computador',
    descricao: 'Enfrente o Stockfish no servidor. Escolha só o nível de dificuldade.',
    icone: 'xadrez'
  },
  {
    id: 'online',
    numero: '02',
    titulo: 'Jogador vs Jogador online',
    subtitulo: 'Multiplayer',
    descricao: 'Entre na sala, desafie quem estiver conectado e jogue em tempo real.',
    icone: 'pessoas'
  },
  {
    id: 'local',
    numero: '03',
    titulo: 'Jogador vs Jogador local',
    subtitulo: 'Mesmo dispositivo',
    descricao: 'Duas pessoas no mesmo aparelho, com relógios e orientação ajustável.',
    icone: 'pessoas'
  },
  {
    id: 'duelo-motores',
    numero: '04',
    titulo: 'CPU vs CPU',
    subtitulo: 'Assista ao confronto',
    descricao: 'Dois níveis de Stockfish jogam entre si. Você só observa.',
    icone: 'servidor'
  }
];

export function Inicio({
  preferencias,
  existePartida,
  historico = [],
  aoEscolherModo,
  aoRetomar,
  aoDescartarPartida,
  aoAbrirAdministracao,
  aoAbrirPreferencias,
  aoAlternarSom,
  aoAbrirSala,
  aoTrocarNome,
  aoEscolherNome,
  aoRemoverNome
}: InicioProps) {
  const nome = preferencias.nomeJogador?.trim() || '';
  const salvos = preferencias.nomesSalvos ?? [];
  const [pedirSenha, setPedirSenha] = useState(false);
  const [senha, setSenha] = useState('');
  const [erroSenha, setErroSenha] = useState('');

  function confirmarAdmin(e?: FormEvent) {
    e?.preventDefault();
    if (autenticarAdmin(senha)) {
      setSenha('');
      setErroSenha('');
      setPedirSenha(false);
      aoAbrirAdministracao();
      return;
    }
    setErroSenha('Senha incorreta.');
  }

  function escolher(modo: ModoJogo) {
    if (modo === 'online' && aoAbrirSala) {
      aoAbrirSala();
      return;
    }
    aoEscolherModo(modo);
  }

  return (
    <main className="tela-inicio">
      <header className="cabecalho-inicio">
        <div className="marca-regia" aria-label="Xadrez">
          <span className="simbolo-marca">
            <Icone nome="coroa" tamanho={28} />
          </span>
          <span>
            <strong>XADREZ</strong>
          </span>
        </div>
        <nav className="acoes-cabecalho" aria-label="Ações principais">
          <button className="botao-icone" type="button" onClick={aoAlternarSom} aria-label={preferencias.somAtivo ? 'Silenciar sons' : 'Ativar sons'}>
            <Icone nome={preferencias.somAtivo ? 'som' : 'mudo'} />
          </button>
          <button className="botao-icone" type="button" onClick={aoAbrirPreferencias} aria-label="Abrir preferências">
            <Icone nome="engrenagem" />
          </button>
        </nav>
      </header>

      <section className="hero-inicio">
        <div className="conteudo-hero">
          <p className="sobretitulo">
            <span /> {nome ? `Olá, ${nome}` : 'O salão está preparado'}
          </p>
          <h1>
            O xadrez como
            <br />
            <em>deveria ser visto.</em>
          </h1>
          <p className="resumo-hero">Jogue contra o computador, contra amigos online ou no mesmo aparelho.</p>
          <div className="assinatura-hero">
            <i />
            <span>Madeira · Marfim · Estratégia</span>
            <i />
          </div>
          <div className="identidade-hero">
            {salvos.length > 0 && (
              <div className="chips-nomes">
                {salvos.map((n) => (
                  <span key={n} className={n === nome ? 'chip-nome-grupo ativo' : 'chip-nome-grupo'}>
                    <button type="button" className="chip-nome" onClick={() => aoEscolherNome?.(n)}>
                      {n}
                    </button>
                    {aoRemoverNome && (
                      <button
                        type="button"
                        className="chip-nome-remover"
                        aria-label={`Remover ${n} deste aparelho`}
                        title="Remover deste aparelho"
                        onClick={(e) => {
                          e.stopPropagation();
                          aoRemoverNome(n);
                        }}
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}
            {aoTrocarNome && (
              <button type="button" className="botao-textual" onClick={aoTrocarNome}>
                {nome ? 'Sair / trocar conta' : 'Entrar com conta'}
              </button>
            )}
          </div>
          {existePartida && (
            <div className="bloco-retomar">
              <button type="button" className="botao-retomar" onClick={aoRetomar}>
                <span className="selo-retomar">
                  <Icone nome="relogio" tamanho={18} />
                </span>
                <span>
                  <small>{nome ? `Partida de ${nome} neste navegador` : 'Partida preservada'}</small>
                  <strong>Retomar do último lance</strong>
                </span>
                <span className="seta-retomar">→</span>
              </button>
              {aoDescartarPartida && (
                <button type="button" className="botao-textual botao-descartar-partida" onClick={aoDescartarPartida}>
                  Descartar partida salva
                </button>
              )}
            </div>
          )}
        </div>
        <div className="cena-hero" aria-hidden="true">
          <div className="halo-hero" />
          <div className="mesa-hero">
            <div className="tabuleiro-hero">
              {Array.from({ length: 36 }, (_, i) => (
                <i key={i} className={(Math.floor(i / 6) + i) % 2 ? 'escura' : 'clara'} />
              ))}
              <span className="hero-peca hero-rei">
                <Peca tipo="k" cor="b" />
              </span>
              <span className="hero-peca hero-dama">
                <Peca tipo="q" cor="w" />
              </span>
              <span className="hero-peca hero-cavalo">
                <Peca tipo="n" cor="w" />
              </span>
              <span className="hero-peca hero-peao">
                <Peca tipo="p" cor="b" />
              </span>
            </div>
          </div>
          <div className="linha-latao linha-latao-a" />
          <div className="linha-latao linha-latao-b" />
        </div>
      </section>

      <section className="selecao-modos" aria-labelledby="titulo-modos">
        <div className="titulo-secao-modos">
          <span>ESCOLHA O MODO</span>
          <h2 id="titulo-modos">Como será a próxima partida?</h2>
        </div>
        <div className="lista-modos">
          {modos.map((modo) => (
            <button type="button" className="faixa-modo" key={modo.id} onClick={() => escolher(modo.id)}>
              <span className="numero-modo">{modo.numero}</span>
              <span className="icone-modo">
                <Icone nome={modo.icone} tamanho={27} />
              </span>
              <span className="texto-modo">
                <small>{modo.subtitulo}</small>
                <strong>{modo.titulo}</strong>
                <em>{modo.descricao}</em>
              </span>
              <span className="abrir-modo">
                Abrir <b>→</b>
              </span>
            </button>
          ))}
        </div>

        {historico.length > 0 && (
          <div className="arquivo-series">
            <div className="titulo-secao-modos" style={{ marginTop: '2rem' }}>
              <span>SEU HISTÓRICO</span>
              <h2>{nome ? `Partidas de ${nome}` : 'Partidas recentes'}</h2>
            </div>
            <div className="lista-historico-jogador">
              {historico.slice(0, 12).map((p) => (
                <article className="cartao-serie" key={p.id}>
                  <header>
                    <strong>
                      {p.nomeBrancas} × {p.nomePretas}
                    </strong>
                    <small>
                      {new Date(p.salvaEm).toLocaleString()} · {p.lances} lances · {p.modo}
                    </small>
                  </header>
                  <p>
                    <em>Resultado:</em> {p.nomeVencedor}
                    {p.motivo ? ` · ${p.motivo}` : ''}
                  </p>
                </article>
              ))}
            </div>
          </div>
        )}
      </section>

      <footer className="rodape-inicio">
        <span>XADREZ</span>
        <i />
        <span>UM JOGO DE PACIÊNCIA E PRECISÃO</span>
        <button type="button" className="link-admin-discreto" onClick={() => setPedirSenha(true)} title="Área restrita">
          ·
        </button>
      </footer>

      {pedirSenha && (
        <div
          className="sobreposicao"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setPedirSenha(false);
          }}
        >
          <form className="modal-confirmacao" role="dialog" aria-modal="true" aria-labelledby="titulo-admin-senha" onSubmit={confirmarAdmin}>
            <span className="selo-modal">
              <Icone nome="escudo" tamanho={28} />
            </span>
            <h2 id="titulo-admin-senha">Área restrita</h2>
            <p>Portal de administração e testes de modelos de linguagem.</p>
            <label className="campo-regio" style={{ textAlign: 'left', width: '100%' }}>
              <span>Senha</span>
              <input
                type="password"
                autoComplete="current-password"
                value={senha}
                onChange={(e) => {
                  setSenha(e.target.value);
                  setErroSenha('');
                }}
                autoFocus
              />
            </label>
            {erroSenha && (
              <p role="alert" style={{ color: '#d4877e', margin: 0 }}>
                {erroSenha}
              </p>
            )}
            <div className="acoes-modal">
              <button type="button" className="botao-contorno" onClick={() => setPedirSenha(false)}>
                Cancelar
              </button>
              <button type="submit" className="botao-principal">
                Entrar
              </button>
            </div>
          </form>
        </div>
      )}
    </main>
  );
}
